import {Range} from 'immutable';
import {Observable, Subject} from 'rxjs';

export const PARALLEL_QUEUE_STRATEGY = {
    ROUND_ROBIN: 'PARALLEL_QUEUE_STRATEGY_ROUND_ROBIN',
    LOAD_BALANCING: 'PARALLEL_QUEUE_STRATEGY_LOAD_BALANCING',
};

/**
 * Converts a higher-order Observable to a first-order Observable by concurrently executing the inner Observables on a number of queues
 * @param levelOfConcurrency Must be a positive number
 * @param strategy Only round robin is supported
 * @param timeToWrapUpObservable An optional observable which will complete all queues when they're done with their current work task
 * @returns first order Observable
 */
function concurrentQueues(levelOfConcurrency, strategy = PARALLEL_QUEUE_STRATEGY.ROUND_ROBIN, timeToWrapUpObservable) {
    if (!Number.isInteger(levelOfConcurrency) || levelOfConcurrency < 2) {
        throw Error('The level of concurrency must be an integer and at least 2');
    }

    let timeToWrapUp = false;

    // Create as many queues as the caller requests
    const queues = Range(0, levelOfConcurrency)
        .toList()
        .map(() => {
            const subject = new Subject();

            return subject.concatMap(innerObs => {
                return Observable.defer(() => {
                    return timeToWrapUp ? Observable.empty() : innerObs;
                });
            });
        });

    if (timeToWrapUpObservable) {
        timeToWrapUpObservable.subscribe(() => {
            timeToWrapUp = true;
        });
    }

    // By calling share we prevent multiple subscriptions to this
    const sharedHigherOrder = this.share();

    if (strategy === PARALLEL_QUEUE_STRATEGY.ROUND_ROBIN) {
        const counting = sharedHigherOrder.scan(acc => acc + 1, 0);

        Observable.zip(sharedHigherOrder, counting).subscribe(([obs, i]) => {
            // Dispatch the inner Observable to one of the queues in a round robin fashion
            queues.get(i % levelOfConcurrency).next(obs);
        });
    } else if (strategy === PARALLEL_QUEUE_STRATEGY.LOAD_BALANCING) {
        // What is the next queue that should receive work?
        const queueNumberSubject = new Subject();

        // Zip a piece of work with the number of the queue that should perform the work
        Observable.zip(sharedHigherOrder, queueNumberSubject).subscribe(([obs, queueNumber]) => {
            // As a side effect on complete, schedule this queue to take more work.
            // Note we have to use do here to not subscribe to the subject prematurely
            const sideEffectingObs = obs.do(null, null, () => {
                queueNumberSubject.next(queueNumber);
            });

            // Dispatch work
            queues.get(queueNumber).next(sideEffectingObs);
        });

        // Initiate all of the queues
        Range(0, levelOfConcurrency)
            .toList()
            .forEach(i => queueNumberSubject.next(i));
    } else {
        throw Error('Unknown concurrency strategy');
    }

    return Observable.merge(...queues.toArray());
}

Observable.prototype.concurrentQueues = concurrentQueues;

Observable.fromPromiseDefer = promiseFunc => Observable.defer(() => Observable.fromPromise(promiseFunc()));
